// Timezone-aware support-hours helper.
// schedule keys are weekday indexes "0"=Sun ... "6"=Sat, values ["HH:MM","HH:MM"]
// in the brand's own tz. "24:00" means end of day.

function partsInTz(date, tz) {
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const map = {};
  for (const p of fmt.formatToParts(date)) map[p.type] = p.value;
  const dayIdx = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 }[map.weekday];
  let hour = parseInt(map.hour, 10);
  if (hour === 24) hour = 0;
  const minute = parseInt(map.minute, 10);
  return { dayIdx, minutes: hour * 60 + minute };
}

function toMinutes(hhmm) {
  const [h, m] = hhmm.split(":").map((n) => parseInt(n, 10));
  return h * 60 + m;
}

const VISITOR_TZ =
  Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York";

export function getVisitorTz() {
  return VISITOR_TZ;
}

// Returns { open: bool, label: string, todayWindow: string|null }
export function computeHours(hours) {
  if (!hours) return { open: null, label: "Hours unavailable", todayWindow: null };
  if (hours.is_24_7) {
    return { open: true, label: "Open now · 24/7", todayWindow: "24 hours a day, every day" };
  }
  const tz = hours.tz || "America/New_York";
  const now = new Date();
  const { dayIdx, minutes } = partsInTz(now, tz);
  const win = hours.schedule?.[String(dayIdx)];
  if (!win) {
    return { open: false, label: "Closed today", todayWindow: null };
  }
  const start = toMinutes(win[0]);
  const end = win[1] === "24:00" ? 24 * 60 : toMinutes(win[1]);
  const open = minutes >= start && minutes < end;

  // Build display window converted to the visitor's timezone for "today".
  const window = formatWindowForVisitor(win, tz);
  return {
    open,
    label: open ? "Open now" : "Closed now",
    todayWindow: window,
  };
}

function formatWindowForVisitor(win, brandTz) {
  // Construct today's start/end as instants in the brand tz, then format in visitor tz.
  const now = new Date();
  const dateStr = new Intl.DateTimeFormat("en-CA", {
    timeZone: brandTz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(now); // YYYY-MM-DD in brand tz

  const startInstant = zonedTimeToInstant(dateStr, win[0], brandTz);
  const endHHMM = win[1] === "24:00" ? "23:59" : win[1];
  const endInstant = zonedTimeToInstant(dateStr, endHHMM, brandTz);
  if (!startInstant || !endInstant) return null;

  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: VISITOR_TZ,
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
  const tzAbbr =
    new Intl.DateTimeFormat("en-US", { timeZone: VISITOR_TZ, timeZoneName: "short" })
      .formatToParts(now)
      .find((p) => p.type === "timeZoneName")?.value || "";
  return `${fmt.format(startInstant)} – ${fmt.format(endInstant)} ${tzAbbr}`;
}

// Approximate a wall-clock time in a given tz to an absolute instant.
function zonedTimeToInstant(dateStr, hhmm, tz) {
  const [y, mo, d] = dateStr.split("-").map((n) => parseInt(n, 10));
  const [h, mi] = hhmm.split(":").map((n) => parseInt(n, 10));
  // Guess in UTC then correct for the tz offset at that time.
  const guess = Date.UTC(y, mo - 1, d, h, mi);
  const asUtc = new Date(guess);
  const offset = tzOffsetMs(asUtc, tz);
  return new Date(guess - offset);
}

function tzOffsetMs(date, tz) {
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
  const parts = {};
  for (const p of dtf.formatToParts(date)) parts[p.type] = p.value;
  let hour = parseInt(parts.hour, 10);
  if (hour === 24) hour = 0;
  const asIfUtc = Date.UTC(
    parseInt(parts.year, 10),
    parseInt(parts.month, 10) - 1,
    parseInt(parts.day, 10),
    hour,
    parseInt(parts.minute, 10),
    parseInt(parts.second, 10)
  );
  return asIfUtc - date.getTime();
}

export function formatVerifiedDate(iso) {
  if (!iso) return "";
  const d = new Date(iso + (iso.length === 10 ? "T00:00:00" : ""));
  if (isNaN(d)) return iso;
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(d);
}
